import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabaseClient";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LineChart,
  Line,
  ResponsiveContainer,
} from "recharts";
import jsPDF from "jspdf";

export default function EasyDocsRecords() {
  const [session, setSession] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [form, setForm] = useState({
    applicant: "",
    job_taken_date: "",
    job_submitted_date: "",
    payment_date: "",
    doc_type: "",
    received_amount: "",
    charges: "",
    expenses: "",
    extra_expenses: "",
    status: "",
  });
  const [authForm, setAuthForm] = useState({ email: "", password: "" });
  const [editingJob, setEditingJob] = useState(null);
  const [released, setReleased] = useState({ Nipun: 0, Irfan: 0, Thilina: 0 });
  const printRef = useRef();

  const viewerEmail = "mohammedirfanct@gmail.com";
  const isViewer = session?.user?.email === viewerEmail;

  // --- Auth ---
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) =>
      setSession(session)
    );
    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => setSession(session)
    );
    return () => listener.subscription.unsubscribe();
  }, []);

  const signIn = async () => {
    const { error } = await supabase.auth.signInWithPassword(authForm);
    if (error) alert(error.message);
  };
  const signOut = async () => {
    await supabase.auth.signOut();
  };

  // --- Jobs ---
  const fetchJobs = async () => {
    const { data, error } = await supabase
      .from("jobs")
      .select("*")
      .order("id", { ascending: false });
    if (!error && data) setJobs(data);
  };

  useEffect(() => {
    if (session) {
      fetchJobs();
      fetchReleasedProfits();
    }
  }, [session]);

  const numberOr0 = (val) => {
    const n = parseFloat(val);
    return Number.isFinite(n) ? n : 0;
  };

  const saveJob = async () => {
    if (isViewer) return;
    const payload = {
      applicant: form.applicant,
      job_taken_date: form.job_taken_date || null,
      job_submitted_date: form.job_submitted_date || null,
      payment_date: form.payment_date || null,
      doc_type: form.doc_type,
      received_amount: numberOr0(form.received_amount),
      charges: numberOr0(form.charges),
      expenses: numberOr0(form.expenses),
      extra_expenses: numberOr0(form.extra_expenses),
      status: form.status,
    };
    if (editingJob) {
      const { error } = await supabase
        .from("jobs")
        .update(payload)
        .eq("id", editingJob.id);
      if (error) {
        alert(error.message);
        return;
      }
      setEditingJob(null);
    } else {
      const { error } = await supabase
        .from("jobs")
        .insert({ ...payload, created_by: session.user.id });
      if (error) {
        alert(error.message);
        return;
      }
    }
    setForm({
      applicant: "",
      job_taken_date: "",
      job_submitted_date: "",
      payment_date: "",
      doc_type: "",
      received_amount: "",
      charges: "",
      expenses: "",
      extra_expenses: "",
      status: "",
    });
    fetchJobs();
  };

  const editJob = (job) => {
    if (isViewer) return;
    setForm({
      applicant: job.applicant || "",
      job_taken_date: job.job_taken_date || "",
      job_submitted_date: job.job_submitted_date || "",
      payment_date: job.payment_date || "",
      doc_type: job.doc_type || "",
      received_amount: job.received_amount?.toString() ?? "",
      charges: job.charges?.toString() ?? "",
      expenses: job.expenses?.toString() ?? "",
      extra_expenses: job.extra_expenses?.toString() ?? "",
      status: job.status || "",
    });
    setEditingJob(job);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (isViewer) return;
    if (window.confirm("Are you sure you want to delete this record?")) {
      const { error } = await supabase.from("jobs").delete().eq("id", id);
      if (error) {
        alert("Delete failed: " + error.message);
        return;
      }
      fetchJobs();
    }
  };

  // --- Calculations ---
  const processedJobs = jobs.map((j) => {
    const charges = numberOr0(j.charges);
    const expenses = numberOr0(j.expenses);
    const extra = numberOr0(j.extra_expenses);
    const totalExpenses = expenses + extra;
    const net = charges - totalExpenses;
    return {
      ...j,
      totalExpenses,
      net,
      nipun: net * 0.5,
      irfan: net * 0.25,
      thilina: net * 0.25,
      received_amount: numberOr0(j.received_amount),
    };
  });

  const totals = processedJobs.reduce(
    (acc, j) => {
      acc.nipun += j.nipun;
      acc.irfan += j.irfan;
      acc.thilina += j.thilina;
      return acc;
    },
    { nipun: 0, irfan: 0, thilina: 0 }
  );

  // Account Balance (deduct released profits)
  const accountBalanceRaw = processedJobs.reduce(
    (acc, j) => acc + numberOr0(j.received_amount) - numberOr0(j.totalExpenses),
    0
  );
  const accountBalance =
    accountBalanceRaw -
    (numberOr0(released.Nipun) +
      numberOr0(released.Irfan) +
      numberOr0(released.Thilina));

  // --- Monthly Aggregation (only Completed jobs) ---
  function formatMonth(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d)) return "";
    return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0");
  }

  const monthlyData = {};
  processedJobs.forEach((j) => {
    if (j.status !== "Completed") return;
    const month = formatMonth(j.job_taken_date);
    if (!month) return;
    if (!monthlyData[month]) {
      monthlyData[month] = {
        month,
        Nipun: 0,
        Irfan: 0,
        Thilina: 0,
        Jobs: 0,
        Received: 0,
      };
    }
    monthlyData[month].Nipun += j.nipun;
    monthlyData[month].Irfan += j.irfan;
    monthlyData[month].Thilina += j.thilina;
    monthlyData[month].Jobs += 1;
    monthlyData[month].Received += numberOr0(j.received_amount);
  });

  const monthlyProfitData = Object.values(monthlyData).sort((a, b) =>
    a.month.localeCompare(b.month)
  );

  const monthlyTotals = monthlyProfitData.reduce(
    (acc, m) => {
      acc.Jobs += m.Jobs;
      acc.Nipun += m.Nipun;
      acc.Irfan += m.Irfan;
      acc.Thilina += m.Thilina;
      acc.Received += m.Received;
      return acc;
    },
    { Jobs: 0, Nipun: 0, Irfan: 0, Thilina: 0, Received: 0 }
  );

  // --- Released Profits ---
  const fetchReleasedProfits = async () => {
    const { data, error } = await supabase.from("released_profits").select("*");
    if (error) {
      console.error("Failed to fetch released profits:", error.message);
      return;
    }
    const totalRow = data.find((r) => r.month === "TOTAL");
    if (totalRow) {
      setReleased({
        Nipun: numberOr0(totalRow.nipun),
        Irfan: numberOr0(totalRow.irfan),
        Thilina: numberOr0(totalRow.thilina),
      });
    }
  };

  const saveReleased = async () => {
    if (isViewer) return;
    const { error } = await supabase.from("released_profits").upsert(
      {
        month: "TOTAL",
        nipun: numberOr0(released.Nipun),
        irfan: numberOr0(released.Irfan),
        thilina: numberOr0(released.Thilina),
      },
      { onConflict: ["month"] }
    );
    if (error) {
      alert("Save failed: " + error.message);
    } else {
      alert("Released profit saved!");
      fetchReleasedProfits();
    }
  };

  // --- Print & PDF Export ---
  const generatePDF = async () => {
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();

    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = "https://i.ibb.co/gMx0z32n/logo-png.png";
      await new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
      });
      if (img.width && img.height) {
        pdf.addImage(img, "PNG", 10, 10, 20, 20);
      }
    } catch (_) {}

    pdf.setFontSize(18);
    pdf.text("EasyDocs Records – Profit Report", pageWidth / 2, 20, {
      align: "center",
    });

    pdf.setFontSize(12);
    pdf.text(
      `Generated on: ${new Date().toLocaleDateString()}`,
      pageWidth / 2,
      30,
      { align: "center" }
    );

    let y = 45;

    pdf.setFontSize(14);
    pdf.text(`Account Balance: ${accountBalance.toFixed(2)}`, 14, y);
    y += 10;

    pdf.setFontSize(14);
    pdf.text("Monthly Totals", 14, y);
    y += 8;

    pdf.setFontSize(10);
    const header = ["Month", "Jobs", "Nipun", "Irfan", "Thilina", "Received"];
    const colX = [14, 50, 70, 95, 120, 150];
    header.forEach((h, i) => pdf.text(h, colX[i], y));
    y += 6;
    pdf.line(14, y, pageWidth - 14, y);
    y += 4;

    const addRow = (cells) => {
      if (y > 280) {
        pdf.addPage();
        y = 20;
      }
      cells.forEach((c, i) => pdf.text(String(c), colX[i], y));
      y += 6;
    };

    monthlyProfitData.forEach((m) => {
      addRow([
        m.month,
        m.Jobs,
        m.Nipun.toFixed(2),
        m.Irfan.toFixed(2),
        m.Thilina.toFixed(2),
        m.Received.toFixed(2),
      ]);
    });

    y += 2;
    pdf.line(14, y, pageWidth - 14, y);
    y += 6;
    addRow([
      "TOTAL",
      monthlyTotals.Jobs,
      monthlyTotals.Nipun.toFixed(2),
      monthlyTotals.Irfan.toFixed(2),
      monthlyTotals.Thilina.toFixed(2),
      monthlyTotals.Received.toFixed(2),
    ]);

    y += 8;
    pdf.setFontSize(14);
    pdf.text("Released Profit", 14, y);
    y += 8;
    pdf.setFontSize(12);
    pdf.text(
      `Nipun: ${numberOr0(released.Nipun).toFixed(2)}   |   Irfan: ${numberOr0(
        released.Irfan
      ).toFixed(2)}   |   Thilina: ${numberOr0(released.Thilina).toFixed(2)}`,
      14,
      y
    );
    y += 12;

    pdf.setFontSize(14);
    pdf.text("Remained Profit", 14, y);
    y += 8;
    pdf.setFontSize(12);
    pdf.text(
      `Nipun: ${(monthlyTotals.Nipun - numberOr0(released.Nipun)).toFixed(
        2
      )}   |   Irfan: ${(monthlyTotals.Irfan - numberOr0(released.Irfan)).toFixed(
        2
      )}   |   Thilina: ${(monthlyTotals.Thilina - numberOr0(
        released.Thilina
      )).toFixed(2)}`,
      14,
      y
    );

    return pdf;
  };

  const handleExportPDF = async () => {
    const pdf = await generatePDF();
    pdf.save("profit-report.pdf");
  };

  const handlePrint = async () => {
    const pdf = await generatePDF();
    const blobUrl = pdf.output("bloburl");
    window.open(blobUrl);
  };

  // --- UI ---
  if (!session) {
    return (
      <div style={{ padding: "2rem", maxWidth: 400, margin: "auto" }}>
        <h2>Login</h2>
        <input
          type="email"
          placeholder="Email"
          onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
        />
        <input
          type="password"
          placeholder="Password"
          onChange={(e) =>
            setAuthForm({ ...authForm, password: e.target.value })
          }
        />
        <button onClick={signIn}>Sign In</button>
      </div>
    );
  }

  return (
    <div style={{ padding: "1rem" }}>
      {/* Header */}
      <header
        style={{
          backgroundColor: "#eaf6ff",
          padding: "0.75rem 1rem",
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          marginBottom: "1rem",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.5rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <img
            src="https://i.ibb.co/gMx0z32n/logo-png.png"
            alt="EasyDocs Logo"
            style={{ height: 45, borderRadius: "6px" }}
            crossOrigin="anonymous"
          />
          <h1 style={{ margin: 0, fontSize: "1.5rem", color: "#004080" }}>
            EasyDocs Records
          </h1>
        </div>
        <button
          onClick={signOut}
          style={{
            padding: "0.5rem 1rem",
            backgroundColor: "#004080",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
          }}
        >
          Sign Out
        </button>
      </header>

      {/* Add Job */}
      {!isViewer && (
        <>
          <h2>{editingJob ? "Edit Job" : "Add New Job"}</h2>
          <form
            className="job-form"
            style={{ display: "grid", gap: "0.75rem", maxWidth: 600 }}
          >
            <label>
              Applicant
              <input
                type="text"
                value={form.applicant}
                onChange={(e) =>
                  setForm({ ...form, applicant: e.target.value })
                }
              />
            </label>
            <label>
              Job Taken Date
              <input
                type="date"
                value={form.job_taken_date}
                onChange={(e) =>
                  setForm({ ...form, job_taken_date: e.target.value })
                }
              />
            </label>
            <label>
              Job Submitted Date
              <input
                type="date"
                value={form.job_submitted_date}
                onChange={(e) =>
                  setForm({ ...form, job_submitted_date: e.target.value })
                }
              />
            </label>
            <label>
              Payment Date
              <input
                type="date"
                value={form.payment_date}
                onChange={(e) =>
                  setForm({ ...form, payment_date: e.target.value })
                }
              />
            </label>
            <label>
              Document Type
              <input
                type="text"
                value={form.doc_type}
                onChange={(e) =>
                  setForm({ ...form, doc_type: e.target.value })
                }
              />
            </label>
            <label>
              Received Amount
              <input
                type="number"
                value={form.received_amount}
                onChange={(e) =>
                  setForm({ ...form, received_amount: e.target.value })
                }
              />
            </label>
            <label>
              Charges
              <input
                type="number"
                value={form.charges}
                onChange={(e) =>
                  setForm({ ...form, charges: e.target.value })
                }
              />
            </label>
            <label>
              Expenses
              <input
                type="number"
                value={form.expenses}
                onChange={(e) =>
                  setForm({ ...form, expenses: e.target.value })
                }
              />
            </label>
            <label>
              Extra Expenses
              <input
                type="number"
                value={form.extra_expenses}
                onChange={(e) =>
                  setForm({ ...form, extra_expenses: e.target.value })
                }
              />
            </label>
            <label>
              Status
              <input
                type="text"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              />
            </label>
            <button type="button" onClick={saveJob}>
              {editingJob ? "Update Job" : "Save Job"}
            </button>
          </form>
        </>
      )}

      {/* Job Records */}
      <h2>Job Records</h2>
      <div style={{ overflowX: "auto" }}>
        <table border="1" cellPadding="5" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th>Applicant</th>
              <th>Job Taken Date</th>
              <th>Job Submitted Date</th>
              <th>Payment Date</th>
              <th>Type</th>
              <th>Received Amount</th>
              <th>Charges</th>
              <th>Expenses</th>
              <th>Extra Expenses</th>
              <th>Total Expenses</th>
              <th>Net</th>
              <th>Nipun</th>
              <th>Irfan</th>
              <th>Thilina</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {processedJobs.map((j) => (
              <tr key={j.id}>
                <td>{j.applicant}</td>
                <td>{j.job_taken_date || ""}</td>
                <td>{j.job_submitted_date || ""}</td>
                <td>{j.payment_date || ""}</td>
                <td>{j.doc_type}</td>
                <td>{numberOr0(j.received_amount).toFixed(2)}</td>
                <td>{numberOr0(j.charges).toFixed(2)}</td>
                <td>{numberOr0(j.expenses).toFixed(2)}</td>
                <td>{numberOr0(j.extra_expenses).toFixed(2)}</td>
                <td>{numberOr0(j.totalExpenses).toFixed(2)}</td>
                <td>
                  {j.net.toFixed(2)} {j.status === "Completed" ? "✅" : ""}
                </td>
                <td>{j.nipun.toFixed(2)}</td>
                <td>{j.irfan.toFixed(2)}</td>
                <td>{j.thilina.toFixed(2)}</td>
                <td>
                  {!isViewer && (
                    <>
                      <button
                        type="button"
                        onClick={() => editJob(j)}
                        style={{
                          marginRight: "0.25rem",
                          padding: "0.25rem 0.5rem",
                          border: "1px solid #004080",
                          backgroundColor: "#eaf6ff",
                          color: "#004080",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(j.id)}
                        style={{
                          padding: "0.25rem 0.5rem",
                          border: "1px solid darkred",
                          backgroundColor: "red",
                          color: "white",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            <tr style={{ fontWeight: "bold", backgroundColor: "#f0f0f0" }}>
              <td colSpan="10" />
              <td>Total</td>
              <td>{totals.nipun.toFixed(2)}</td>
              <td>{totals.irfan.toFixed(2)}</td>
              <td>{totals.thilina.toFixed(2)}</td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>

      {/* Account Balance */}
      <div style={{ margin: "1rem 0", overflowX: "auto" }}>
        <table border="1" cellPadding="5" style={{ borderCollapse: "collapse", width: "100%" }}>
          <tbody>
            <tr style={{ fontWeight: "bold", backgroundColor: "#f9f9f9" }}>
              <td>Account Balance</td>
              <td>{accountBalance.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Job Status */}
      <h2>Job Status</h2>
      <div style={{ overflowX: "auto" }}>
        <table border="1" cellPadding="5" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th>Applicant Name</th>
              <th>Document Type</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {processedJobs.map((j) => (
              <tr key={j.id}>
                <td>{j.applicant}</td>
                <td>{j.doc_type}</td>
                <td>{j.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Charts + Monthly Totals */}
      <div ref={printRef}>
        <h2>Monthly Profit Distribution</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={monthlyProfitData}>
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="Nipun" stackId="a" fill="#8884d8" />
            <Bar dataKey="Irfan" stackId="a" fill="#82ca9d" />
            <Bar dataKey="Thilina" stackId="a" fill="#ffc658" />
          </BarChart>
        </ResponsiveContainer>

        <h2>Monthly Job Trend</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={monthlyProfitData}>
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="Jobs" stroke="#ff7300" />
          </LineChart>
        </ResponsiveContainer>

        <h2>Monthly Totals</h2>
        <div style={{ overflowX: "auto" }}>
          <table border="1" cellPadding="5" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th>Month</th>
                <th>Jobs</th>
                <th>Nipun</th>
                <th>Irfan</th>
                <th>Thilina</th>
                <th>Received Total</th>
              </tr>
            </thead>
            <tbody>
              {monthlyProfitData.map((m) => (
                <tr key={m.month}>
                  <td>{m.month}</td>
                  <td>{m.Jobs}</td>
                  <td>{m.Nipun.toFixed(2)}</td>
                  <td>{m.Irfan.toFixed(2)}</td>
                  <td>{m.Thilina.toFixed(2)}</td>
                  <td>{m.Received.toFixed(2)}</td>
                </tr>
              ))}
              <tr style={{ fontWeight: "bold", backgroundColor: "#f0f0f0" }}>
                <td>Total</td>
                <td>{monthlyTotals.Jobs}</td>
                <td>{monthlyTotals.Nipun.toFixed(2)}</td>
                <td>{monthlyTotals.Irfan.toFixed(2)}</td>
                <td>{monthlyTotals.Thilina.toFixed(2)}</td>
                <td>{monthlyTotals.Received.toFixed(2)}</td>
              </tr>
              <tr style={{ backgroundColor: "#e0ffe0" }}>
                <td>Released Profit Amount</td>
                <td></td>
                <td>
                  {isViewer ? (
                    released.Nipun
                  ) : (
                    <input
                      type="number"
                      value={released.Nipun}
                      onChange={(e) =>
                        setReleased({ ...released, Nipun: e.target.value })
                      }
                    />
                  )}
                </td>
                <td>
                  {isViewer ? (
                    released.Irfan
                  ) : (
                    <input
                      type="number"
                      value={released.Irfan}
                      onChange={(e) =>
                        setReleased({ ...released, Irfan: e.target.value })
                      }
                    />
                  )}
                </td>
                <td>
                  {isViewer ? (
                    released.Thilina
                  ) : (
                    <input
                      type="number"
                      value={released.Thilina}
                      onChange={(e) =>
                        setReleased({ ...released, Thilina: e.target.value })
                      }
                    />
                  )}
                </td>
                <td>
                  {!isViewer && <button onClick={saveReleased}>Save</button>}
                </td>
              </tr>
              <tr style={{ backgroundColor: "#ffe0e0" }}>
                <td>Remained Profit</td>
                <td></td>
                <td>{(monthlyTotals.Nipun - numberOr0(released.Nipun)).toFixed(2)}</td>
                <td>{(monthlyTotals.Irfan - numberOr0(released.Irfan)).toFixed(2)}</td>
                <td>{(monthlyTotals.Thilina - numberOr0(released.Thilina)).toFixed(2)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Buttons */}
      <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
        {!isViewer && <button onClick={handlePrint}>Print Report</button>}
        {!isViewer && <button onClick={handleExportPDF}>Export as PDF</button>}
      </div>
    </div>
  );
}
